import React, { Component } from "react";
import {
  withModulesManager,
  withHistory,
  formatMessage,
} from "@openimis/fe-core";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Typography,
  Paper,
  Box,
  Grid,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  MenuItem,
} from "@material-ui/core";
import { ArrowBack as BackIcon, Save as SaveIcon } from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";
import {
  fetchInsureeAccountClaimEnrollment,
  updateInsureeAccountClaimEnrollment,
} from "../../actions";
import {
  normalizeClaimData,
  toBackendClaimPayload,
} from "../../utils/enrollmentData";

const styles = (theme) => ({
  page: { margin: theme.spacing(2) },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: { fontWeight: 700, color: theme.palette.primary.main, flex: 1 },
  paper: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  actions: { display: "flex", justifyContent: "flex-end", gap: theme.spacing(1) },
});

const DOC_TYPES = ["NATIONAL_ID", "PASSPORT", "OTHER"];

class InsureeAccountClaimEdit extends Component {
  state = {
    form: {
      fullName: "",
      dob: "",
      chfId: "",
      notes: "",
      identityDocumentType: "NATIONAL_ID",
      enrollmentNotes: "",
    },
    isLoading: false,
    errors: {},
  };

  componentDidMount() {
    const uuid = this.props.match?.params?.enrollmentId;
    if (uuid) {
      this.props.fetchInsureeAccountClaimEnrollment(
        this.props.modulesManager,
        uuid
      );
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.enrollment &&
      this.props.enrollment !== prevProps.enrollment
    ) {
      const e = this.props.enrollment;
      const d = normalizeClaimData(e.claimData);
      this.setState({
        form: {
          fullName: d.fullName || "",
          dob: (d.dob || "").toString().slice(0, 10),
          chfId: d.chfId || "",
          notes: d.notes || "",
          identityDocumentType: e.identityDocumentType || "NATIONAL_ID",
          enrollmentNotes: e.notes || "",
        },
      });
    }
  }

  handleChange = (field) => (event) => {
    const value = event.target.value;
    this.setState((prev) => ({
      form: { ...prev.form, [field]: value },
      errors: { ...prev.errors, [field]: undefined },
    }));
  };

  handleBack = () => {
    const uuid = this.props.enrollment?.uuid || this.props.match?.params?.enrollmentId;
    const { history } = this.props;
    if (history && uuid) history.push(`/enrollment/account-claim/${uuid}/detail`);
    else if (history) history.push("/enrollment/account-claim");
  };

  validate = () => {
    const { form } = this.state;
    const errors = {};
    if (!form.chfId) errors.chfId = "CHF ID is required";
    if (!form.fullName) errors.fullName = "Full name is required";
    if (!form.dob) errors.dob = "Date of birth is required";
    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSave = () => {
    if (!this.validate()) return;
    const { form } = this.state;
    const uuid = this.props.enrollment?.uuid;
    if (!uuid) return;
    this.setState({ isLoading: true });
    const payload = {
      uuid,
      claimData: toBackendClaimPayload(form),
      identityDocumentType: form.identityDocumentType,
      notes: form.enrollmentNotes,
    };
    Promise.resolve(
      this.props.updateInsureeAccountClaimEnrollment(
        this.props.modulesManager,
        payload,
        "Update Account Claim Enrollment"
      )
    )
      .then(() => {
        this.setState({ isLoading: false });
        this.handleBack();
      })
      .catch(() => this.setState({ isLoading: false }));
  };

  render() {
    const { classes, intl, enrollment } = this.props;
    const { form, errors, isLoading } = this.state;

    if (!enrollment && !form.chfId) {
      return (
        <Box className={classes.page} display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box className={classes.page}>
        <Box className={classes.header}>
          <IconButton onClick={this.handleBack}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5" className={classes.title}>
            {formatMessage(intl, "enrollment", "accountClaimEnrollment.edit") ||
              "Edit Account Claim"}
          </Typography>
        </Box>

        <Paper className={classes.paper}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="CHF ID"
                value={form.chfId}
                onChange={this.handleChange("chfId")}
                error={!!errors.chfId}
                helperText={errors.chfId}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Full name"
                value={form.fullName}
                onChange={this.handleChange("fullName")}
                error={!!errors.fullName}
                helperText={errors.fullName}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Date of birth"
                InputLabelProps={{ shrink: true }}
                value={form.dob}
                onChange={this.handleChange("dob")}
                error={!!errors.dob}
                helperText={errors.dob}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Identity document type"
                value={form.identityDocumentType}
                onChange={this.handleChange("identityDocumentType")}
              >
                {DOC_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Claim notes"
                value={form.notes}
                onChange={this.handleChange("notes")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Enrollment notes"
                value={form.enrollmentNotes}
                onChange={this.handleChange("enrollmentNotes")}
              />
            </Grid>
          </Grid>
        </Paper>

        <Box className={classes.actions}>
          <Button onClick={this.handleBack}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={this.handleSave}
            disabled={isLoading}
          >
            Save
          </Button>
        </Box>
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  enrollment: state.enrollment?.accountClaimEnrollmentDetail,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchInsureeAccountClaimEnrollment,
      updateInsureeAccountClaimEnrollment,
    },
    dispatch
  );

export default withModulesManager(
  withHistory(
    injectIntl(
      withStyles(styles)(
        connect(mapStateToProps, mapDispatchToProps)(InsureeAccountClaimEdit)
      )
    )
  )
);
